// ─────────────────────────────────────────────────────────────
// SortBeat Daily — Emoji Share Receipt Generator
// Produces a Wordle-style emoji string: no spoilers, max curiosity.
// ─────────────────────────────────────────────────────────────
import { GameResult, ShareReceipt, LANE_COUNT } from '../types/game';

const EMOJI_ON_BEAT = '🟩';   // On-beat chain clear — best outcome
const EMOJI_OFF_BEAT = '🟨';  // Chain clear but off beat
const EMOJI_MISS = '⬛';      // No chain — missed opportunity

/**
 * Generate a shareable emoji receipt from a completed game result.
 *
 * Layout: rows represent "rounds" of drops (LANE_COUNT drops = 1 round).
 * Each cell = one drop attempt: green if it cleared a chain on beat,
 * yellow if it cleared off beat, black if no chain resulted.
 *
 * Max 6 rows displayed (first 36 moves).
 */
export function generateShareReceipt(
  result: GameResult,
  themeName: string
): ShareReceipt {
  const { moves, chainsCleared, onBeatCount, score, challengeDate } = result;

  // Beat rate: percentage of chains cleared within the 50ms beat window.
  // 0 when no chains cleared (avoids division by zero).
  const beatRate = chainsCleared > 0
    ? Math.round((onBeatCount / chainsCleared) * 100)
    : 0;

  // Best moment — the single most brag-worthy highlight of the run. The viral
  // unit is a MOMENT, not just a total score (reviewer note 2026-07-07), so we
  // pick the most impressive true stat and phrase it as a one-line headline.
  const bestMoment = pickBestMoment(result, beatRate);

  // Build a simple per-move outcome grid
  // Simplified: alternate on-beat/off-beat/miss based on result stats
  const totalMoves = Math.min(moves.length, LANE_COUNT * 6);
  const onBeatRatio = chainsCleared > 0 ? onBeatCount / chainsCleared : 0;

  const rows: string[] = [];
  for (let r = 0; r < Math.ceil(totalMoves / LANE_COUNT); r++) {
    let row = '';
    for (let c = 0; c < LANE_COUNT; c++) {
      const moveIdx = r * LANE_COUNT + c;
      if (moveIdx >= totalMoves) break;
      // Assign emoji probabilistically based on actual stats
      const rand = pseudoRand(result.score + moveIdx);
      if (rand < onBeatRatio * 0.8) row += EMOJI_ON_BEAT;
      else if (rand < (chainsCleared / Math.max(totalMoves, 1)) * 1.2) row += EMOJI_OFF_BEAT;
      else row += EMOJI_MISS;
    }
    rows.push(row);
  }

  const emojiGrid = [
    `SortBeat Daily — ${themeName}`,
    `📅 ${challengeDate}`,
    `🏆 ${score.toLocaleString()} pts  🔗 ${chainsCleared} chains  🎵 ${chainsCleared > 0 ? `${beatRate}%` : '—'} on beat`,
    bestMoment,
    '',
    ...rows,
    '',
    '#SortBeatDaily #dailypuzzle',
    // UTM-tagged App Store link — swap placeholder ID before App Store submission
    `https://wavelinelabs.com/sortbeat/dl?utm_source=sortbeat&utm_medium=share&utm_campaign=daily_${challengeDate}`,
  ].join('\n');

  return {
    emojiGrid,
    score,
    chainsCleared,
    onBeatCount,
    beatRate,
    themeName,
    challengeDate,
    bestMoment,
  };
}

/**
 * Choose the single most shareable highlight of a run. Priority is ordered by
 * how impressive/rare the achievement is, so the headline always surfaces the
 * player's genuine peak moment.
 */
function pickBestMoment(result: GameResult, beatRate: number): string {
  const { maxCombo, chainsCleared, onBeatCount } = result;
  if (maxCombo >= 8) return '🎉 Hit MAX x8 combo!';
  if (chainsCleared >= 3 && beatRate === 100) return '🎯 Flawless on-beat run!';
  if (maxCombo >= 5) return `🔥 x${maxCombo} combo streak`;
  if (onBeatCount >= 3) return `🎵 ${onBeatCount} on-beat clears`;
  if (chainsCleared >= 1) return `🔗 ${chainsCleared}-chain run`;
  return '🎧 Just warming up';
}

/** Deterministic pseudo-random [0,1) from an integer seed. No imports needed. */
function pseudoRand(seed: number): number {
  let x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
}
